// Session Manager for DEFCON TERMINAL
// Handles local storage and session persistence

import type { GameState, PlayerStatistics } from '@shared/types';
import { apiClient } from './ApiClient';

const STORAGE_KEYS = {
  OPERATOR_ID: 'defcon_operator_id',
  SESSION_TOKEN: 'defcon_session_token',
  HANDLE: 'defcon_handle',
  GAME_STATE: 'defcon_game_state',
  AUDIO_MUTED: 'defcon_audio_muted',
  PREFERENCES: 'defcon_preferences',
} as const;

interface SessionData {
  operatorId: string;
  handle: string;
  sessionToken: string;
  gameState: GameState;
  statistics: PlayerStatistics;
}

interface Preferences {
  audioMuted: boolean;
  typingSpeed: 'slow' | 'normal' | 'fast' | 'instant';
  colorScheme: 'green' | 'amber' | 'white';
}

const DEFAULT_PREFERENCES: Preferences = {
  audioMuted: false,
  typingSpeed: 'normal',
  colorScheme: 'green',
};

const DEFAULT_GAME_STATE: GameState = {
  currentPath: null,
  currentScenario: null,
  currentNode: null,
  decisions: [],
  completedScenarios: [],
  discoveredEasterEggs: [],
  totalPlayTime: 0,
  scenarioStartTime: null,
};

const DEFAULT_STATISTICS: PlayerStatistics = {
  gamesPlayed: 0,
  scenariosCompleted: 0,
  nuclearWarsPrevented: 0,
  nuclearWarsTriggered: 0,
  covertMissionsSucceeded: 0,
  covertMissionsFailed: 0,
  highestScore: 0,
  totalCasualties: 0,
  peacefulResolutions: 0,
};

class SessionManager {
  private currentSession: SessionData | null = null;
  private preferences: Preferences = DEFAULT_PREFERENCES;
  private saveTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.loadPreferences();
  }

  // Check if there's an existing session
  hasExistingSession(): boolean {
    return !!localStorage.getItem(STORAGE_KEYS.OPERATOR_ID);
  }

  // Get stored operator ID for display
  getStoredOperatorId(): string | null {
    return localStorage.getItem(STORAGE_KEYS.OPERATOR_ID);
  }

  getStoredHandle(): string | null {
    return localStorage.getItem(STORAGE_KEYS.HANDLE);
  }

  // Create new player session
  async createNewSession(handle: string): Promise<SessionData> {
    const response = await apiClient.createPlayer(handle);

    const session: SessionData = {
      operatorId: response.operatorId,
      handle,
      sessionToken: response.sessionToken,
      gameState: { ...DEFAULT_GAME_STATE },
      statistics: { ...DEFAULT_STATISTICS },
    };

    this.currentSession = session;
    this.persistSession();

    return session;
  }

  // Load existing session
  async loadExistingSession(operatorId: string): Promise<SessionData> {
    // First, try to load from local storage (for offline/mock sessions)
    const storedSession = this.loadSessionFromStorage();

    if (storedSession && storedSession.operatorId === operatorId) {
      // Check if this is a mock session (offline play)
      if (storedSession.sessionToken.startsWith('mock-token-')) {
        // Mock session - restore from local storage without API validation
        this.currentSession = storedSession;
        return storedSession;
      }

      // Try API validation for non-mock sessions
      try {
        const response = await apiClient.loadPlayer(operatorId);

        const session: SessionData = {
          operatorId,
          handle: response.handle,
          sessionToken: response.sessionToken,
          gameState: response.gameState,
          statistics: response.statistics,
        };

        this.currentSession = session;
        this.persistSession();

        return session;
      } catch (error) {
        // API validation failed - fall back to local storage for mock sessions
        if (storedSession.sessionToken.startsWith('mock-token-')) {
          this.currentSession = storedSession;
          return storedSession;
        }
        throw error; // Re-throw for non-mock sessions
      }
    }

    // No local session found, try API
    const response = await apiClient.loadPlayer(operatorId);

    const session: SessionData = {
      operatorId,
      handle: response.handle,
      sessionToken: response.sessionToken,
      gameState: response.gameState,
      statistics: response.statistics,
    };

    this.currentSession = session;
    this.persistSession();

    return session;
  }

  // Try to restore session from local storage
  async tryRestoreSession(): Promise<SessionData | null> {
    const operatorId = localStorage.getItem(STORAGE_KEYS.OPERATOR_ID);
    if (!operatorId) return null;

    // First check if we have a local session stored
    const localSession = this.loadSessionFromStorage();

    // If it's a mock/offline session, restore it directly
    if (localSession && localSession.sessionToken.startsWith('mock-token-')) {
      this.currentSession = localSession;
      return localSession;
    }

    try {
      return await this.loadExistingSession(operatorId);
    } catch (error) {
      // Session invalid, clear local storage
      this.clearSession();
      return null;
    }
  }

  // Get current session
  getCurrentSession(): SessionData | null {
    return this.currentSession;
  }

  // Update game state
  updateGameState(updates: Partial<GameState>): void {
    if (!this.currentSession) {
      throw new Error('No active session');
    }

    this.currentSession.gameState = {
      ...this.currentSession.gameState,
      ...updates,
    };

    this.persistSession();
    this.scheduleSaveToServer();
  }

  // Add decision to history
  addDecision(scenarioId: string, nodeId: string, choice: string): void {
    if (!this.currentSession) return;

    this.currentSession.gameState.decisions.push({
      scenarioId,
      nodeId,
      choice,
      timestamp: Date.now(),
    });

    this.persistSession();
    this.scheduleSaveToServer();
  }

  // Mark scenario as completed
  completeScenario(scenarioId: string): void {
    if (!this.currentSession) return;

    if (!this.currentSession.gameState.completedScenarios.includes(scenarioId)) {
      this.currentSession.gameState.completedScenarios.push(scenarioId);
    }

    this.currentSession.statistics.scenariosCompleted++;
    this.persistSession();
  }

  // Discover easter egg
  discoverEasterEgg(eggId: string): boolean {
    if (!this.currentSession) return false;

    if (this.currentSession.gameState.discoveredEasterEggs.includes(eggId)) {
      return false; // Already discovered
    }

    this.currentSession.gameState.discoveredEasterEggs.push(eggId);
    this.persistSession();
    return true;
  }

  // Set mock session for offline play
  setMockSession(handle: string, operatorId: string): void {
    const session: SessionData = {
      operatorId,
      handle,
      sessionToken: 'mock-token-' + Date.now(),
      gameState: { ...DEFAULT_GAME_STATE },
      statistics: { ...DEFAULT_STATISTICS },
    };

    this.currentSession = session;
    this.persistSession();
  }

  // Clear session (logout)
  clearSession(): void {
    this.currentSession = null;
    localStorage.removeItem(STORAGE_KEYS.OPERATOR_ID);
    localStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.HANDLE);
    localStorage.removeItem(STORAGE_KEYS.GAME_STATE);
    apiClient.clearSessionToken();
  }

  // Preferences
  getPreferences(): Preferences {
    return { ...this.preferences };
  }

  updatePreferences(updates: Partial<Preferences>): void {
    this.preferences = { ...this.preferences, ...updates };
    localStorage.setItem(STORAGE_KEYS.PREFERENCES, JSON.stringify(this.preferences));
  }

  isAudioMuted(): boolean {
    return this.preferences.audioMuted;
  }

  setAudioMuted(muted: boolean): void {
    this.updatePreferences({ audioMuted: muted });
  }

  // Private methods
  private persistSession(): void {
    if (!this.currentSession) return;

    localStorage.setItem(STORAGE_KEYS.OPERATOR_ID, this.currentSession.operatorId);
    localStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, this.currentSession.sessionToken);
    localStorage.setItem(STORAGE_KEYS.HANDLE, this.currentSession.handle);
    localStorage.setItem(STORAGE_KEYS.GAME_STATE, JSON.stringify(this.currentSession.gameState));
  }

  private loadPreferences(): void {
    const stored = localStorage.getItem(STORAGE_KEYS.PREFERENCES);
    if (stored) {
      try {
        this.preferences = { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) };
      } catch {
        this.preferences = DEFAULT_PREFERENCES;
      }
    }
  }

  private loadSessionFromStorage(): SessionData | null {
    const operatorId = localStorage.getItem(STORAGE_KEYS.OPERATOR_ID);
    const handle = localStorage.getItem(STORAGE_KEYS.HANDLE);
    const sessionToken = localStorage.getItem(STORAGE_KEYS.SESSION_TOKEN);
    const gameStateJson = localStorage.getItem(STORAGE_KEYS.GAME_STATE);

    if (!operatorId || !handle || !sessionToken || !gameStateJson) {
      return null;
    }

    try {
      return {
        operatorId,
        handle,
        sessionToken,
        gameState: JSON.parse(gameStateJson),
        statistics: { ...DEFAULT_STATISTICS }, // Restore from persisted game state if available
      };
    } catch {
      return null;
    }
  }

  private scheduleSaveToServer(): void {
    // Debounce saves to server - save after 2 seconds of inactivity
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }

    this.saveTimeout = setTimeout(async () => {
      if (this.currentSession) {
        try {
          await apiClient.saveGame(this.currentSession.gameState);
        } catch (error) {
          console.error('Failed to save game to server:', error);
        }
      }
    }, 2000);
  }
}

// Singleton instance
export const sessionManager = new SessionManager();
export default sessionManager;
